/**
 * Handlers centralizados para obtener opciones de los selects de MBZ.
 *
 * Este archivo agrupa las llamadas a APIs de entidades productivas (zonas, granos, campañas)
 * utilizadas en los formularios de creación, edición y filtros de MBZ.
 */

import {
  CampanaProductiva,
  getAllCampanasProductivas,
  getGranosProductivos,
  getZonasProductivasActivas,
  GranoProductivo,
  ZonaProductiva,
} from "@/services/productive-entities.service";

/**
 * Opciones disponibles para los selects de MBZ.
 */
export interface ZonalGrossMarginOptions {
  zones: ZonaProductiva[];
  grains: GranoProductivo[];
  campaigns: CampanaProductiva[];
}

interface FetchZonalGrossMarginOptionsProps {
  applyPPMBFilterToGrains?: boolean;
}

/**
 * Obtiene todas las opciones necesarias para los selects de MBZ en una sola llamada.
 * Ejecuta en paralelo las consultas de zonas, granos y campañas.
 *
 * Endpoints utilizados:
 * - GET /ZonasProductivas/activas - Zonas productivas activas
 * - GET /GranosProductivos - Granos productivos (con filtro opcional AplicaPPMB)
 * - GET /CampanasProductivas - Todas las campañas productivas (activas e inactivas)
 *
 * @param applyPPMBFilterToGrains - Si es true, filtra granos que aplican a PPMB
 * @returns Objeto con arrays de zonas, granos y campañas
 */
export const fetchZonalGrossMarginOptionsHandler = async ({
  applyPPMBFilterToGrains = false,
}: FetchZonalGrossMarginOptionsProps = {}): Promise<ZonalGrossMarginOptions> => {
  const [zones, grains, campaigns] = await Promise.all([
    getZonasProductivasActivas(),
    getGranosProductivos(applyPPMBFilterToGrains ? { AplicaPPMB: true } : {}),
    getAllCampanasProductivas(),
  ]);

  return {
    zones: zones ?? [],
    grains: grains ?? [],
    campaigns: campaigns ?? [],
  };
};
